import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { libraryNav } from '@/components/px/nav'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import {
  eurShort,
  healthRowClass,
  healthTone,
  isoDayLabel,
  phaseDisplayName,
} from '@/features/projects/format'
import { PortfolioEmpty, PortfolioError, PortfolioLoading } from '@/features/projects/components/PortfolioStates'
import {
  clearProjectsError,
  fetchProjects,
  selectProjectsError,
  selectProjectsItems,
  selectProjectsLoading,
} from '@/features/projects/slices/projectsSlice'
import type { ProjectSummary } from '@/features/projects/types'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * Surface 2.1 — the portfolio board, WIRED (PX-W6.3, AC-1): one row per
 * project the server says this account may read. The table furniture and the
 * delivery-cut phase names are the prototype's own; the DATA is the
 * ACL-intersected, caller-redacted list. Tab partitioning, counts and the
 * KPI strip are DERIVED from the loaded rows — no backend aggregate exists,
 * so nothing is faked (spec OQ-3). `/projects/health` stays demo-badged and
 * makes zero network calls.
 */
/**
 * The board's columns. PX-W6.6 moved them out of the demo content module: the
 * table renders LIVE `ProjectSummary` rows, so its headers are product copy in
 * EN and NL, keyed under `portfolio.columns.*` (invariant I-2).
 */
const COLUMNS = [
  { key: 'project', numeric: false },
  { key: 'office', numeric: false },
  { key: 'phase', numeric: false },
  { key: 'health', numeric: false },
  { key: 'lead', numeric: false },
  { key: 'contract', numeric: true },
  { key: 'goLive', numeric: false },
] as const

function PortfolioTable({ rows }: { rows: readonly ProjectSummary[] }) {
  const { t } = useTranslation('portfolio')
  return (
    <Table>
      <TableCaption className="sr-only">{t('tableCaption')}</TableCaption>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          {COLUMNS.map((column) => (
            <TableHead
              key={column.key}
              scope="col"
              className={cn(
                'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
                column.numeric && 'text-right'
              )}
            >
              {t(`columns.${column.key}`)}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id} className={healthRowClass(row.health)}>
            <TableCell>
              <div className="flex items-center gap-2">
                <Link
                  to={`/project/${row.id}`}
                  className="font-medium text-foreground underline-offset-4 hover:underline"
                >
                  {row.client ?? row.name}
                </Link>
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {row.odooVersion !== undefined && row.vertical !== undefined
                  ? `Odoo ${row.odooVersion} · ${row.vertical}`
                  : (row.description ?? row.name)}
              </div>
            </TableCell>
            <TableCell>
              <Badge variant="outline" className="font-normal text-ink-soft">
                {row.office ?? '—'}
              </Badge>
            </TableCell>
            <TableCell>
              <StatusPill tone="neutral">{phaseDisplayName(row.currentPhase)}</StatusPill>
            </TableCell>
            <TableCell>
              <StatusPill tone={healthTone(row.health)}>{row.health ?? '—'}</StatusPill>
            </TableCell>
            {/* The wire row carries no lead; the office badge stands in for the chip. */}
            <TableCell>
              <Badge variant="outline" className="font-normal text-ink-soft">
                {row.office ?? t('cells.noLead')}
              </Badge>
            </TableCell>
            <TableCell className="v-figure text-right tabular-nums text-foreground">
              {row.contractValue !== undefined ? eurShort(row.contractValue) : t('cells.noContract')}
            </TableCell>
            <TableCell className="v-figure tabular-nums text-ink-soft">
              {row.goLiveDate !== undefined && row.goLiveDate !== ''
                ? isoDayLabel(row.goLiveDate)
                : t('cells.noGoLive')}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

type TabKey = 'active' | 'ams' | 'won'

export default function PortfolioBoardPage() {
  const { t } = useTranslation('portfolio')
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const items = useAppSelector(selectProjectsItems)
  const loading = useAppSelector(selectProjectsLoading)
  const error = useAppSelector(selectProjectsError)

  useEffect(() => {
    if (orgId !== null) void dispatch(fetchProjects(orgId))
  }, [dispatch, orgId])

  // Tab partitioning — re-derived from the loaded rows, never faked (D5).
  const partitions = useMemo(() => {
    const active = items.filter(
      (row) => row.status !== 'archived' && row.status !== 'won'
    )
    const ams = items.filter((row) => row.contractType === 'AMS contract')
    const won = items.filter((row) => row.status === 'won')
    return { active, ams, won } as Record<TabKey, ProjectSummary[]>
  }, [items])

  const kpis = useMemo(() => {
    const atRisk = partitions.active.filter(
      (row) => row.health === 'At risk' || row.health === 'Blocked'
    ).length
    const contracted = partitions.active.reduce((sum, row) => sum + (row.contractValue ?? 0), 0)
    const offices = new Set(items.map((row) => row.office).filter((office): office is string => office !== undefined))
    return { atRisk, contracted, offices: offices.size }
  }, [items, partitions.active])

  const contextPill = t('contextPill', { active: partitions.active.length, offices: kpis.offices })

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: 'active', label: t('tabs.active'), count: partitions.active.length },
    { key: 'ams', label: t('tabs.ams'), count: partitions.ams.length },
    { key: 'won', label: t('tabs.won'), count: partitions.won.length },
  ]

  const onRetryClicked = (): void => {
    dispatch(clearProjectsError())
    if (orgId !== null) void dispatch(fetchProjects(orgId))
  }

  return (
    <AppShell
      moduleLabel={t('chrome.moduleLabel')}
      breadcrumb={t('chrome.breadcrumb')}
      contextPill={contextPill}
      nav={libraryNav}
    >
      <header className="max-w-3xl">
        <h1 className="font-serif text-2xl font-semibold text-foreground">{t('title')}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{t('lede')}</p>
      </header>

      <div className="mt-5">
        {loading && items.length === 0 ? (
          <PortfolioLoading />
        ) : error !== null ? (
          <PortfolioError error={error} onRetry={onRetryClicked} />
        ) : items.length === 0 ? (
          <PortfolioEmpty />
        ) : (
          <Tabs defaultValue="active">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <TabsList>
                {tabs.map((tab) => (
                  <TabsTrigger key={tab.key} value={tab.key} className="gap-1.5">
                    {tab.label}
                    <span className="v-figure tabular-nums text-muted-foreground">{tab.count}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              <ul className="flex items-center gap-3" aria-label={t('sections.grouping')}>
                {[t('chips.groupByPhase'), t('chips.allOffices')].map((chip) => (
                  <li key={chip}>
                    <span aria-disabled="true" title={t('chips.withheldTitle')} className="text-xs text-muted-foreground">
                      {chip}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <TabsContent value="active" className="mt-3">
              <div className="rounded-lg border border-border bg-card">
                <PortfolioTable rows={partitions.active} />
              </div>
            </TabsContent>

            <TabsContent value="ams" className="mt-3">
              <div className="rounded-lg border border-border bg-card">
                <PortfolioTable rows={partitions.ams} />
              </div>
            </TabsContent>

            <TabsContent value="won" className="mt-3">
              <div className="rounded-lg border border-border bg-card">
                <PortfolioTable rows={partitions.won} />
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      <section className="mt-8" aria-label={t('sections.indicators')}>
        <div className="flex items-baseline justify-between gap-3">
          <SectionLabel>{t('sections.label')}</SectionLabel>
          <span className="text-xs text-muted-foreground">{t('kpis.derivedNote')}</span>
        </div>
        <div className="mt-2 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label={t('kpis.inDelivery')}
            figure={String(partitions.active.length)}
            qualifier={t('kpis.inDeliveryQualifier', { value: eurShort(kpis.contracted) })}
          />
          <KpiCard
            label={t('kpis.atRisk')}
            figure={String(kpis.atRisk)}
            qualifier={t('kpis.atRiskQualifier')}
          />
          <KpiCard
            label={t('kpis.ams')}
            figure={String(partitions.ams.length)}
            qualifier={t('kpis.amsQualifier')}
          />
          <KpiCard
            label={t('kpis.won')}
            figure={String(partitions.won.length)}
            qualifier={t('kpis.wonQualifier')}
          />
        </div>
      </section>

      <DensityNote>{t('density.note')}</DensityNote>
    </AppShell>
  )
}
